import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { EnvironmentService } from '@src/app/core/services/environment.service';
import { FILE_UPLOAD } from '@src/app/features/shared/constants/file-upload';
import { Ocr } from '@src/app/features/web-submission/models/ocr.model';
import { getImagePreviewSrcByBlob, isNullOrEmpty } from 'projects/sigv-library/src/util/src/public-api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiFileCenterService, SystemDialogService, UserProfileService } from 'sigv-library';
import { OCRService } from './services/ocr.service';

@Component({
  selector: 'app-ocr',
  templateUrl: './ocr.component.html',
  styleUrls: ['./ocr.component.scss']
})
export class OCRComponent implements OnInit, OnDestroy {

  @Input() showOCRButton = false;
  @Input() submissionNo = -1;
  @Input() isDialogMode = true;
  @Input() needWebcam = false;
  @Input() ocrRecord: Ocr = {};
  @Output() scoreEvent = new EventEmitter();
  @Output() imgEvent = new EventEmitter();
  isShowQueryDialog = false;
  dateFormat = '';
  facingMode = 'environment';

  private pdfScrollTriggered = false;

  videoWidth = Math.min((80 * window.innerWidth) / 100, 500);
  videoHeight = Math.min((80 * window.innerWidth) / 100, 500);

  submissionOCRICCardRequired = false;
  submissionOCRPaySlipRequired = false;

  pdfView?: SafeResourceUrl;
  pdfViewString: string = '';

  validationResults: {
    status: boolean | null;
    date: string;
  } = {
      status: null, // api 回傳結果
      date: '', // api 回傳結果
    };

  oCRRecords: [
    {
      title: string;
      name: string;
      img: any;
      src: SafeUrl | null;
      blob: Blob | null;
      showCam: boolean;
      stream: MediaStream | null;
      showUpload: boolean;
      camErr: boolean;
      type: string;
      pdfViewString: string;
    },
    {
      title: string;
      name: string;
      img: any;
      src: SafeUrl | null;
      blob: Blob | null;
      showCam: boolean;
      stream: MediaStream | null;
      showUpload: boolean;
      camErr: boolean;
      type: string;
      pdfViewString: string;
    }
  ] = [
      {
        title: 'Common.Validate.OCRIDCard',
        name: 'IDcard',
        img: null,
        src: null,
        blob: null,
        showCam: false,
        stream: null,
        showUpload: true,
        camErr: false,
        type: 'image/jpeg',
        pdfViewString: '',
      },
      {
        title: 'Common.Validate.OCRPaySlip',
        name: 'PaySlip',
        img: null,
        src: null,
        blob: null,
        showCam: false,
        stream: null,
        showUpload: true,
        camErr: false,
        type: 'image/jpeg',
        pdfViewString: '',
      },
    ];

  private readonly destroy$ = new Subject();

  constructor(
    private apiFileCenterService: ApiFileCenterService,
    private userProfileService: UserProfileService,
    private sanitizer: DomSanitizer,
    private dialogService: SystemDialogService,
    private translateService: TranslateService,
    private ocrService: OCRService
  ) {
    this.submissionOCRICCardRequired = EnvironmentService.environmentConfig.submissionOCRICCardRequired;
    this.submissionOCRPaySlipRequired = EnvironmentService.environmentConfig.submissionOCRPaySlipRequired;

   }

  ngOnInit(): void {
    this.initial();
    this.GetOCRRecord();
  }

  async startCamera(recordName: string, videoElement: any, canvas: any) {
    const record = this.oCRRecords.find((x) => x.name === recordName);
    try {
      this.oCRRecords.forEach((x) => {
        if (x.name !== recordName) {
          x.showCam = false;
          if (x.stream) {
            x.stream.getTracks().forEach((track) => track.stop());
            videoElement.pause();
            videoElement.srcObject = null;
          }
        }
      });
      if (record) {
        record.showCam = !record?.showCam;
        if (record.showCam) {
          console.log('Trigger Cam' + record);
          record.stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: this.facingMode, // 'environment' 表示使用後置鏡頭，'user' 表示使用前置鏡頭
            },
          });
          videoElement.srcObject = record.stream;
          videoElement.load();
          videoElement.play();
        } else {
          if (record.stream) {
            record.stream.getTracks().forEach((track) => track.stop());
            videoElement.pause();
            videoElement.srcObject = null;
          }
        }
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      if(record){
        record.camErr = true;
        record.showCam = false;
      }
    }
  }

  async switchCamera(recordName: string, videoElement: any) {
    const record = this.oCRRecords.find((x) => x.name === recordName);
    if (record) {
    try {
        if (record.stream) {
          record.stream.getTracks().forEach((track) => track.stop());
          videoElement.pause();
          videoElement.srcObject = null;
        }
        if (record.showCam) {
          if (this.facingMode === 'environment') {
            this.facingMode = 'user';
          } else if (this.facingMode === 'user') {
            this.facingMode = 'environment';
          }
          console.log('Switch Cam:' + this.facingMode);
          record.stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: this.facingMode, // 'environment' 表示使用後置鏡頭，'user' 表示使用前置鏡頭
            },
          });
          videoElement.srcObject = record.stream;
          videoElement.load();
          videoElement.play();
        }
      } catch (error) {
        console.error('Error accessing camera:', error);
        if(record){
          record.camErr = true;
          record.showCam = false;
        }
      }
    }
  }

  capturePhoto(recordName: string, videoElement: any, canvas: any) {
    const context = canvas.getContext('2d');
    context.canvas.width = videoElement.videoWidth;
    context.canvas.height = videoElement.videoHeight;
    context.drawImage(
      videoElement,
      0,
      0,
      videoElement.videoWidth,
      videoElement.videoHeight
    );

    canvas.toBlob((blob: Blob) => {
      const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
      // 接下來的處理
      this.apiFileCenterService
        .uploadFile({
          domain: 'WebSubmission',
          subDomain: 'OCR',
          creator: this.userProfileService.currentUserValue?.userNo || '',
          description: 'WebSubmission OCR ' + recordName + ' File',
          file,
          sortIndex: 1,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: any) => {
          var record = this.oCRRecords.find((x) => x.name === recordName);
          if (record) {
            record.img = result;
            record.src = this.createSaveUrl(file);
            record.blob = file;
            console.log('Captured photo:', record.src);
            this.imgEvent.emit({
              idImageGuid: this.oCRRecords.find((x) => x.name === 'IDcard')?.img,
              paySlipImgGuid: this.oCRRecords.find((x) => x.name === 'PaySlip')?.img,
              idType: this.oCRRecords.find((x) => x.name === 'IDcard')?.type,
              payType: this.oCRRecords.find((x) => x.name === 'PaySlip')?.type,
            });
          }
        });
    }, 'image/jpeg');
  }

  async GetOCRRecord() {
    let idImage = '';
    let payImage = '';
    let idType = '';
    let payType = '';
    if(this.submissionNo > 0){
      var record = await this.ocrService.GetOCRRecord({submissionNo: this.submissionNo}).toPromise();
      idImage = record.data?.idImage ?? '';
      payImage = record.data?.payImage ?? '';
      idType = record.data?.idType ?? '';
      payType = record.data?.payType ?? '';
    }

    if (!isNullOrEmpty(this.ocrRecord.idImage) && this.ocrRecord.idImage != undefined) {
      idImage = this.ocrRecord.idImage;
      idType = this.ocrRecord.idType ?? '';
    }
    if(!isNullOrEmpty(this.ocrRecord.payImage) && this.ocrRecord.payImage != undefined){
      payImage = this.ocrRecord.payImage;
      payType = this.ocrRecord.payType ?? '';
    }
    this.imgEvent.emit({
      idImageGuid: idImage,
      paySlipImgGuid: payImage,
      idType: idType,
      payType: payType,
    });

    this.apiFileCenterService
      .getFileDownloadAsync({
        fileDetailId: payImage,
        downloader: this.userProfileService.currentUserValue?.userNo!,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((photoResponse: any) => {
        const url = photoResponse
          ? this.createSaveUrl(photoResponse)
          : '';

        var photo = this.oCRRecords.find((x) => x.name === 'PaySlip')!;
        if (payType.includes('pdf')) {
          const res =
            getImagePreviewSrcByBlob(photoResponse) +
            '#toolbar=0&scrollbar=0&navpanes=0';
          this.pdfView = this.sanitizer.bypassSecurityTrustResourceUrl(res);
    
          // worker.js 路徑需要對齊 angular.json 中 build 的路徑
          (window as any).pdfWorkerSrc = '/pdf.worker.js'
    
          photo.pdfViewString = this.sanitizer.sanitize(
            SecurityContext.RESOURCE_URL,
            this.pdfView
          ) ?? '';
          console.log('pdfViewString', photo.pdfViewString);
        }
        else{
          photo.img = payImage;
        }
        photo.src = url;
        photo.blob = photoResponse;
        photo.type = payType;
        
      });

      this.apiFileCenterService
        .getFileDownloadAsync({
          fileDetailId: idImage,
          downloader: this.userProfileService.currentUserValue?.userNo!,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe((idCardResponse: any) => {
          const url = idCardResponse
            ? this.createSaveUrl(idCardResponse)
            : '';

          var idCard = this.oCRRecords.find((x) => x.name === 'IDcard')!;
          if (idType.includes('pdf')) {
            const res =
              getImagePreviewSrcByBlob(idCardResponse) +
              '#toolbar=0&scrollbar=0&navpanes=0';
            this.pdfView = this.sanitizer.bypassSecurityTrustResourceUrl(res);
      
            // worker.js 路徑需要對齊 angular.json 中 build 的路徑
            (window as any).pdfWorkerSrc = '/pdf.worker.js'
      
            idCard.pdfViewString = this.sanitizer.sanitize(
              SecurityContext.RESOURCE_URL,
              this.pdfView
            ) ?? '';
            console.log('pdfViewString', idCard.pdfViewString);
          }
          else{
            idCard.img = idImage;
          }
          idCard.src = url;
          idCard.blob = idCardResponse;
          idCard.type = idType;
        });
  }

  initial(): void {
    this.validationResults = {
      status: null,
      date: '',
    };
    this.oCRRecords = [
      {
        title: 'Common.Validate.OCRIDCard',
        name: 'IDcard',
        img: null,
        src: null,
        blob: null,
        showCam: false,
        stream: null,
        showUpload: true,
        camErr: false,
        type: 'image/jpeg',
        pdfViewString: '',
      },
      {
        title: 'Common.Validate.OCRPaySlip',
        name: 'PaySlip',
        img: null,
        src: null,
        blob: null,
        showCam: false,
        stream: null,
        showUpload: true,
        camErr: false,
        type: 'image/jpeg',
        pdfViewString: '',
      },
    ];
  }

  CancelDialog() {
    this.isShowQueryDialog = false;

    this.oCRRecords.forEach((x) => {
      x.showCam = false;
    });
  }

  /**
   * 開啟彈跳視窗
   */
  showQueryDialog() {
    this.isShowQueryDialog = true;

    this.initial();
    this.GetOCRRecord();
  }

  ConvertToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event: ProgressEvent<FileReader>) => {
        if (event.target && event.target.result) {
          var base64String: string = event.target.result as string;
          const regex = /data:.*base64,/;
          var outputString = base64String.replace(regex, '');
          resolve(outputString);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read Blob as Base64.'));
      };

      reader.readAsDataURL(blob);
    });
  }

  /**
   * 選擇上傳檔案
   */
  selectedUploadSignatureFile(target: any, recordName: string): void {
    this.validationResults.status = null;
    const file: File = target.files[0];
    const acceptFileExtensions = target.accept;
    const record = this.oCRRecords.find((x) => x.name === recordName);
    console.log('File Type:',file.type);
    if (file == undefined) {
      return;
    }

    // Validate File extension
    const isFileTypeValidate = this.validateAcceptFileType(
      [file.name],
      acceptFileExtensions
    );
    if (!isFileTypeValidate) {
      this.dialogService.showErrorDialog([
        {
          description: this.translateService.instant(
            'Common.Validate.OCRValidateAccept',
            {
              type: this.translateService.instant(record?.title!),
            }
          ),
        },
      ]);
      return;
    }

    // Validate File Size
    const isFileSizeValidate = this.validateFilesMaxSize([file.size]);
    if (!isFileSizeValidate) {
      this.dialogService.showErrorDialog([
        {
          description: this.translateService.instant(
            'Common.Validate.OCRValidateSize',
            {
              type: this.translateService.instant(record?.title!),
            }
          ),
        },
      ]);
      return;
    }

    this.apiFileCenterService
      .uploadFile({
        domain: 'WebSubmission',
        subDomain: 'OCR',
        creator: this.userProfileService.currentUserValue?.userNo || '',
        description: 'WebSubmission OCR ' + recordName + ' File',
        file,
        sortIndex: 1,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (result: any) => {
        var record = this.oCRRecords.find((x) => x.name === recordName);
        if (record) {
          record.img = result;
          record.src = this.createSaveUrl(file);
          record.blob = file;
          record.type = file.type;
          this.imgEvent.emit({
            idImageGuid: this.oCRRecords.find((x) => x.name === 'IDcard')?.img,
            paySlipImgGuid: this.oCRRecords.find((x) => x.name === 'PaySlip')?.img,
            idType: this.oCRRecords.find((x) => x.name === 'IDcard')?.type,
            payType: this.oCRRecords.find((x) => x.name === 'PaySlip')?.type,
            submissionNo: this.submissionNo,
          });
          const url =
            getImagePreviewSrcByBlob(file) +
            '#toolbar=0&scrollbar=0&navpanes=0';
          this.pdfView = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    
          // worker.js 路徑需要對齊 angular.json 中 build 的路徑
          (window as any).pdfWorkerSrc = '/pdf.worker.js'
    
          record.pdfViewString = this.sanitizer.sanitize(
            SecurityContext.RESOURCE_URL,
            this.pdfView
          ) ?? '';
          console.log('pdfViewString', record.pdfViewString);
        }
      });
  }

  /**
   * Validate file size
   */
  private validateFilesMaxSize(fileSizes: number[]): boolean {
    if (fileSizes && fileSizes.length > 0) {
      var min = FILE_UPLOAD.eKYCFileMinSizeLimit * 1024;
      var max = FILE_UPLOAD.eKYCFileMaxSizeLimit * (1024 * 1024);
      return !fileSizes.find((size) => !(size >= min && size <= max));
    } else {
      return true;
    }
  }

  /**
   * Validate file extension
   * @param fileNames
   * @param accept accept file extension split by comma
   * @returns
   */
  private validateAcceptFileType(fileNames: string[], accept: string): boolean {
    const accepts = accept
      .split(',')
      .map((extensionName) => {
        if (extensionName) {
          return extensionName.replace(/\./gm, '').trim();
        } else {
          return '';
        }
      })
      .filter((e) => !!e);
    if (accepts && accepts.length > 0) {
      const ruleString = `\\.(${accepts.join('|')})+$`;
      const regex = new RegExp(ruleString);

      return fileNames.every((fileName) => {
        if (fileName) {
          const checkFileName = fileName.toLocaleLowerCase();
          return regex.test(checkFileName);
        } else {
          return false;
        }
      });
    }
    return true;
  }

  /**
   * 轉換File to Url
   */
  createSaveUrl(file: Blob): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(
      getImagePreviewSrcByBlob(file)
    );
  }

  ngOnDestroy(): void {
    this.oCRRecords.forEach((x) => {
      x.showCam = false;
      if (x.stream) {
        x.stream.getTracks().forEach((track) => track.stop());
      }
    });
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPdfLoaded(pdf: any) {
    setTimeout(() => {
      const container = document.querySelector('.ng2-pdf-viewer-container');
      if (!container) return;
  
      container.addEventListener('scroll', () => {
        if (this.pdfScrollTriggered) return;
  
        const isBottom =
          container.scrollTop + container.clientHeight >= container.scrollHeight - 5;
  
        if (isBottom) {
          this.pdfScrollTriggered = true;
        }
      });
    }, 200);
  }

}
